/**
 * EPIC 40 TND — hooks TanStack Query du module « Appels d'offres ».
 *
 * ## Doctrine d'invalidation
 *
 * Toute mutation invalide `tendersKeys.tenders.all` **ET** les clés brutes du
 * DataTable serveur (`['/api/sales/tenders']`, `['datatable-groups', …]`,
 * `['datatable-facets']` et `['datatable-aggregations']`) : le socle met en
 * cache sous la chaîne d'endpoint, et ne l'invalider qu'à moitié laisse une
 * partie de l'écran peinte sur des données périmées pendant que le reste s'est
 * rafraîchi — la barre d'état du bas a ainsi continué d'additionner des fiches
 * supprimées.
 *
 * S'y ajoutent les surfaces d'un AUTRE module qui montrent le même fait : le
 * sous-onglet « Appels d'offres » de la fiche client (TND-022) et son KPI. Sans
 * elles, un AO passé à « Gagné » laisserait la fiche client afficher l'ancien
 * état jusqu'à un rechargement complet.
 */

import { useCallback } from 'react'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'

import { apiFetch } from '@/lib/api'
import type { BulkImpact } from '@/components/ui/data-table/actions/action-types'
import { toBulkImpact, type BulkImpactPayload } from '@/lib/bulk-impact'
import { crmKeys } from '@/lib/queryKeys.crm'
import { tendersKeys } from '@/lib/queryKeys.tenders'

import {
    TENDERS_ENDPOINT,
    handleTenderError,
    type ProcedureType,
    type ProcedureTypesPayload,
    type TenderChecklist,
    type TenderDeadline,
    type TenderDetail,
    type TenderOutcomeReason,
    type TenderLotDetail,
    type TenderPiece,
    type TenderPriceGrid,
    type TenderPriceLine,
    type TenderQuestion,
    type TenderStatus,
    type TenderWatchSummary,
    type WatchProfile,
} from './useTenders.types'
import type { RepriseDUnLot } from './useTenders.types.recopie'
import { optionsDesDocuments, type FiltresDesDocuments } from './useTenders.documentsPages'
import { lireTousLesEchanges } from './useTenders.questionsPages'
import { parametresDeReaffectation, type CiblesDeReaffectation } from './useTenders.reaffectation'

const LIST_KEY = ['/api/sales/tenders']
const GROUPS_KEY = ['datatable-groups', '/api/sales/tenders']

/**
 * Refuse de partir avec `?entity_id=` VIDE.
 *
 * Les écrans lisent l'entité courante en `currentEntity?.entityId ?? ''` : tant
 * que `/auth/me` n'a pas répondu, cette valeur est la chaîne vide, et le serveur
 * répond « entity_id query param or X-Entity-Id header required » — un message
 * d'API que l'utilisateur voit tel quel après avoir simplement cliqué trop tôt.
 */
function assertEntityInUrl(url: string): void {
    if (/[?&]entity_id=(&|$)/.test(url)) {
        throw new Error(
            "L'organisme n'est pas encore chargé. Patientez une seconde puis réessayez.",
        )
    }
}

/**
 * `init` sert aux routes qui portent l'entité en EN-TÊTE plutôt qu'en query —
 * les endpoints CRM `…/clients/{id}/activities/*`, qui acceptent les deux et
 * dont le jumeau `useClientProposals` utilise déjà `x-entity-id`.
 */
async function getJson<T>(url: string, init?: RequestInit): Promise<T> {
    assertEntityInUrl(url)
    const res = await apiFetch(url, init)
    if (!res.ok) await handleTenderError(res)
    return res.json() as Promise<T>
}

async function sendJson<T>(url: string, method: string, body?: unknown): Promise<T | null> {
    assertEntityInUrl(url)
    const res = await apiFetch(url, {
        method,
        headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
        body: body !== undefined ? JSON.stringify(body) : undefined,
    })
    if (!res.ok) await handleTenderError(res)
    if (res.status === 204) return null
    return res.json() as Promise<T>
}

/** Invalide listing serveur + groupes + facettes + agrégats + (option) fiche + fiche client. */
export function invalidateTenders(qc: QueryClient, tenderId?: string) {
    qc.invalidateQueries({ queryKey: tendersKeys.tenders.all, refetchType: 'all' })
    qc.invalidateQueries({ queryKey: LIST_KEY, refetchType: 'all' })
    qc.invalidateQueries({ queryKey: GROUPS_KEY, refetchType: 'all' })
    qc.invalidateQueries({ queryKey: ['datatable-facets'], refetchType: 'all' })
    // 🔴 La barre d'état du BAS — `['datatable-aggregations', aggregationEndpoint,
    // params]`, cf. `components/ui/data-table/aggregations/StatusBar.tsx`. C'est là
    // que la table range son total de lignes, la somme des montants estimés et ses
    // moyennes. Elle n'était invalidée QUE par l'édition en ligne d'une cellule
    // (`DataTable.tsx`), jamais par une écriture venue d'ailleurs : après une
    // suppression en masse, le pied de tableau continuait d'additionner des fiches
    // déjà disparues de la table au-dessus.
    // Par PRÉFIXE, comme les facettes juste au-dessus : l'endpoint d'agrégats n'est
    // pas déductible du listing (`<listing>/aggregations` ici, jamais garanti), et
    // les `params` de la clé portent la colonne agrégée choisie par l'utilisateur.
    // `refetchType: 'all'` comme ses voisines : le défaut `'active'` ne rejoue pas
    // une requête dont le composant est démonté — et la table l'est dès qu'on part
    // sur une fiche.
    qc.invalidateQueries({ queryKey: ['datatable-aggregations'], refetchType: 'all' })
    if (tenderId) {
        qc.invalidateQueries({
            queryKey: tendersKeys.tenders.detailAny(tenderId),
            refetchType: 'all',
        })
    }
    // Le kanban lit les MÊMES appels d'offres par un autre chemin : ses
    // en-têtes de colonne et ses tuiles doivent tomber avec la table, sinon un
    // AO déplacé réapparaît dans sa colonne d'origine au retour sur la vue.
    qc.invalidateQueries({ queryKey: tendersKeys.tenderBoards.all, refetchType: 'all' })
    // TND-022 — le sous-onglet « Appels d'offres » de la fiche client et son
    // KPI montrent ces mêmes lignes. On invalide TOUTES les fiches : le client
    // porteur n'est pas toujours connu ici, et un rattachement peut en changer deux.
    qc.invalidateQueries({ queryKey: [...crmKeys.clients.all, 'tenders'], refetchType: 'all' })
}

// ── Référentiels ─────────────────────────────────────────────────────────────

/** Les 6 procédures de passation. Référentiel PUBLIC : pas d'entité en clé. */
export function useProcedureTypes() {
    return useQuery<ProcedureType[]>({
        queryKey: tendersKeys.tenders.referentials(),
        // La route rend `{ procedures: [...] }` : on déballe ICI, une fois, pour
        // que tous les appelants reçoivent un TABLEAU. Laisser l'enveloppe
        // remonter obligeait chaque écran à s'en souvenir — et le premier qui
        // l'oublie tombe sur « procedures.map is not a function ».
        queryFn: async () => {
            const payload = await getJson<ProcedureTypesPayload>(
                `${TENDERS_ENDPOINT}/tenders/referentials`,
            )
            return payload.procedures ?? []
        },
        // Réglementaire et versionné en base : il ne bouge pas d'une session.
        staleTime: 60 * 60 * 1000,
    })
}

export function useTenderStatuses(entityId: string) {
    return useQuery<TenderStatus[]>({
        queryKey: tendersKeys.tenderStatuses.list(entityId),
        queryFn: () =>
            getJson<TenderStatus[]>(`${TENDERS_ENDPOINT}/tender-statuses?entity_id=${entityId}`),
        enabled: Boolean(entityId),
        staleTime: 5 * 60 * 1000,
    })
}

/** Étiquette d'une valeur de facette — ce que le panneau de filtres affiche. */
export interface TenderFacetOption {
    value: string
    label: string
}

/**
 * 🔑 **La clé sous laquelle les ÉTIQUETTES voyagent, à côté des compteurs.**
 *
 * Elle doit rester identique au `CLE_DES_ETIQUETTES` du serveur
 * (`backend/src/infra/tenders_repository/tenders_analytics.py`). Elle ne nomme
 * aucune colonne — c'est ce qui la rend inerte pour le socle DataTable, qui
 * indexe la réponse par `column.key`.
 */
const CLE_DES_ETIQUETTES = '__etiquettes__'

/** Ce que la route sert : les compteurs par colonne, PLUS les étiquettes. */
type ReponseDesFacettes = Record<string, Record<string, number>> & {
    [CLE_DES_ETIQUETTES]?: Record<string, TenderFacetOption[]>
}

/**
 * Le VOCABULAIRE des filtres dont la valeur est un identifiant de personne.
 *
 * 🔴 **Pourquoi pas `useUsers()`.** Ce hook-là rend la PREMIÈRE page de
 * `/admin/users` : 50 comptes, triés par identifiant, donc les 50 plus anciens —
 * au 51ᵉ collaborateur les suivants sortaient du filtre « Chargé de réponse » ;
 * et la route exige `users:read`, si bien qu'un chargé de réponse sans droits
 * d'administration ouvrait une liste VIDE sur une colonne visible par défaut.
 *
 * Les facettes, elles, ne demandent que `tenders:read` et portent les titulaires
 * du périmètre visible, entités FILLES comprises. Sans filtre : ce sont les
 * options POSSIBLES, pas celles du résultat courant.
 *
 * ⚠️ **Ce hook ne veut QUE la moitié « étiquettes ».** La route sert deux moitiés
 * dans le même objet ; indexer la réponse par `facetOptions?.['owner_id']` sans
 * ce `select` rendrait la table des COMPTEURS, sur laquelle l'appelant ferait un
 * `.filter()` — et `getJson` ne valide aucune forme, donc `tsc` resterait vert.
 */
export function useTenderFilterOptions(entityId: string) {
    return useQuery<ReponseDesFacettes, Error, Record<string, TenderFacetOption[]>>({
        queryKey: [...tendersKeys.tenders.all, 'filter-options', entityId],
        queryFn: () =>
            getJson<ReponseDesFacettes>(
                `${TENDERS_ENDPOINT}/tenders/facets?entity_id=${entityId}`,
            ),
        select: (brut) => brut?.[CLE_DES_ETIQUETTES] ?? {},
        enabled: !!entityId,
        staleTime: 60_000,
    })
}

/**
 * Les motifs d'issue — le référentiel qu'on choisit en clôturant un AO.
 *
 * @param kind Les motifs applicables à ce `kind` (`LOST` / `CLOSED`) : « Note technique »
 *   n'a aucun sens sur un abandon. Le serveur porte l'applicabilité, l'écran ne la rejoue pas.
 * @param stage L'étape VISÉE (portée par étape, décision PO n° 3) : « Offres irrégulières »
 *   est un fait de l'acheteur, pas un motif d'abandon. Filtrée par le prédicat de la garde.
 * @param withCounts Ramène `tenders_count` : distingue « supprimable » de « seulement
 *   retirable » — supprimer un motif porté trouerait les statistiques qui le citent.
 */
export function useTenderOutcomeReasons(
    entityId: string,
    options: { kind?: string; stage?: string | null; withCounts?: boolean } = {},
) {
    const { kind, stage, withCounts } = options
    return useQuery<TenderOutcomeReason[]>({
        queryKey: [
            ...tendersKeys.tenderStatuses.outcomeReasons(entityId),
            kind ?? 'all',
            stage ?? 'toutes-etapes',
            withCounts ? 'counts' : 'plain',
        ],
        queryFn: () => {
            const p = new URLSearchParams({ entity_id: entityId })
            if (kind) p.set('kind', kind)
            if (stage) p.set('stage', stage)
            if (withCounts) p.set('with_counts', 'true')
            return getJson<TenderOutcomeReason[]>(
                `${TENDERS_ENDPOINT}/tender-outcome-reasons?${p}`,
            )
        },
        enabled: Boolean(entityId),
        staleTime: 5 * 60 * 1000,
    })
}

// ── Fiche ────────────────────────────────────────────────────────────────────

export function useTenderDetail(tenderId: string | null, entityId: string) {
    return useQuery<TenderDetail>({
        queryKey: tendersKeys.tenders.detail(tenderId ?? '', entityId),
        queryFn: () =>
            getJson<TenderDetail>(
                `${TENDERS_ENDPOINT}/tenders/${tenderId}?entity_id=${entityId}`,
            ),
        enabled: Boolean(tenderId && entityId),
        staleTime: 15_000,
    })
}

export function useCreateTender(entityId: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (body: Record<string, unknown>) =>
            sendJson<TenderDetail>(
                `${TENDERS_ENDPOINT}/tenders?entity_id=${entityId}`,
                'POST',
                body,
            ),
        onSuccess: () => invalidateTenders(qc),
    })
}

export function useUpdateTender(tenderId: string, entityId: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (body: Record<string, unknown>) =>
            sendJson<TenderDetail>(
                `${TENDERS_ENDPOINT}/tenders/${tenderId}?entity_id=${entityId}`,
                'PATCH',
                body,
            ),
        onSuccess: () => invalidateTenders(qc, tenderId),
    })
}

/**
 * L'endpoint UNIQUE de transition — table, kanban et fiche y passent tous.
 *
 * Le corps peut porter le bloc `deadline_inputs` (date et mode de notification
 * du rejet, échéance des justificatifs, délai d'alerte de recandidature) : ce
 * sont les faits que la modale recueille au moment où l'utilisateur les
 * connaît, et ils ARMENT les échéances réglementaires. Sans eux, la règle
 * correspondante ne s'exécute pas — on n'invente pas une date de notification.
 */
/**
 * TND-003 — enregistre la décision de RÉPONDRE.
 *
 * Deux gestes, un seul point d'entrée serveur. « On y répond » avance à
 * « Questions » dans la même transaction ; « on ne répond pas » clôt la
 * consultation en « Abandonné », motif obligatoire. Les deux sont donc des
 * transitions : on invalide exactement ce que `useChangeTenderStatus` invalide,
 * échéances comprises — une clôture en désarme.
 */
export function useRecordBidDecision(tenderId: string, entityId: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (body: {
            decision: 'GO' | 'NO_GO'
            outcome_reason_id?: string | null
            outcome_notes?: string | null
        }) =>
            sendJson<TenderDetail>(
                `${TENDERS_ENDPOINT}/tenders/${tenderId}/bid-decision?entity_id=${entityId}`,
                'POST',
                body,
            ),
        onSuccess: () => {
            invalidateTenders(qc, tenderId)
            qc.invalidateQueries({ queryKey: tendersKeys.tenders.deadlines(tenderId) })
        },
    })
}

/**
 * Change l'étape d'un appel d'offres **DÉSIGNÉ AU MOMENT DE L'APPEL**.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 POURQUOI CETTE VARIANTE EXISTE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `useChangeTenderStatus(tenderId, …)` compose son URL **à la création du
 * hook**, donc au rendu. C'est juste pour une fiche — l'identifiant y vient de
 * la route et ne change pas. Ça ne l'est pas du tout pour le KANBAN, qui le
 * découvre au moment du dépôt.
 *
 * Le kanban faisait `setDragTenderId(cardId)` puis appelait aussitôt la
 * mutation. Or l'état posé n'est visible qu'au rendu SUIVANT : la fermeture qui
 * s'exécute porte encore l'ancienne valeur. Conséquences mesurables :
 *
 * - **le premier dépôt** d'une session partait sur `/tenders//status` — chaîne
 *   vide — et rendait une erreur que rien n'expliquait ;
 * - **chaque dépôt suivant** déplaçait la carte PRÉCÉDENTE. Le serveur écrivait
 *   un événement de transition parfaitement valide, sur le mauvais dossier, et
 *   l'écran annonçait un succès.
 *
 * Aucun test ne pouvait le voir : le hook est correct, l'appelant est correct
 * ligne à ligne, et c'est leur COMPOSITION qui ment.
 */
export function useChangeTenderStatusOf(entityId: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            tenderId,
            ...body
        }: { tenderId: string } & Record<string, unknown>) =>
            sendJson<TenderDetail>(
                `${TENDERS_ENDPOINT}/tenders/${tenderId}/status?entity_id=${entityId}`,
                'POST',
                body,
            ),
        onSuccess: (_data, { tenderId }) => {
            invalidateTenders(qc, tenderId)
            // Une transition peut AVOIR CRÉÉ des échéances réglementaires :
            // le rail les affiche, il doit les relire.
            qc.invalidateQueries({ queryKey: tendersKeys.tenders.deadlines(tenderId) })
        },
    })
}

/** La même transition, sur un appel d'offres connu au RENDU (une fiche). */
export function useChangeTenderStatus(tenderId: string, entityId: string) {
    const mutation = useChangeTenderStatusOf(entityId)
    return {
        ...mutation,
        mutateAsync: (body: Record<string, unknown>) =>
            mutation.mutateAsync({ tenderId, ...body }),
    }
}

/**
 * Suppression PHYSIQUE d'un appel d'offres.
 *
 * ⚠️ Le serveur ne l'accepte qu'au SEUIL du pipeline (stage « Veille ») et sur
 * une fiche sans contenu : au-delà, l'AO a une histoire — un DCE retiré, des
 * questions posées, un pli déposé — et l'effacer ferait disparaître la trace
 * d'une candidature qui a existé. Une issue défavorable est un STATUT terminal,
 * jamais une suppression. Le refus (409) NOMME ce qui bloque.
 */
export function useDeleteTender(entityId: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (tenderId: string) =>
            sendJson<null>(
                `${TENDERS_ENDPOINT}/tenders/${tenderId}?entity_id=${entityId}`,
                'DELETE',
            ),
        onSuccess: () => invalidateTenders(qc),
    })
}

// ── TND-004 §8 — actions en MASSE ────────────────────────────────────────────

/**
 * Préfixe des clés i18n des motifs d'exclusion.
 *
 * Le serveur ne renvoie qu'une CLÉ stable (`NOT_IN_TRIAGE`) : la phrase vit ici,
 * en FR et en EN. On ajoute des motifs, on n'en renomme pas — une clé renommée
 * casse l'aperçu de tous les lots en cours d'écriture.
 *
 * **Sans point final** : `toBulkImpact` le pose.
 */
export const TENDER_BULK_REASON_PREFIX = 'sales.tenders.bulk.reason'

/**
 * L'aperçu d'impact — appelé à l'OUVERTURE du dialogue de confirmation, jamais
 * à chaque case cochée. Même route, `?dry_run=true`, donc **même éligibilité**
 * que l'exécution : le nombre annoncé est celui qui sera appliqué.
 *
 * ⚠️ `set_status` n'y figure pas : le serveur le refuse en 400
 * `BULK_PREVIEW_UNSUPPORTED`. Ses refus se décident DANS la transition —
 * checklist incomplète, motif manquant — et les rejouer pour un aperçu créerait
 * une seconde implémentation des gardes.
 */
export function useTendersBulkImpact(entityId: string) {
    return useCallback(
        async (
            action: 'delete' | 'reassign',
            ids: string[],
            params: Record<string, unknown> = {},
        ): Promise<BulkImpact> => {
            const data = await sendJson<BulkImpactPayload>(
                `${TENDERS_ENDPOINT}/tenders/bulk?entity_id=${entityId}&dry_run=true`,
                'POST',
                { ids, action, params },
            )
            if (!data) throw new Error('empty preview')
            return toBulkImpact(data, TENDER_BULK_REASON_PREFIX)
        },
        [entityId],
    )
}

export interface TenderBulkResult {
    success: number
    skipped: number
    /** Chaque refus est NOMMÉ : « 3 échecs » sans dire lesquels oblige à tout
     *  rejouer à la main. */
    errors: { id: string; code: string; message?: string | null }[]
}

function useTenderBulk(entityId: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (body: { ids: string[]; action: string; params?: Record<string, unknown> }) =>
            sendJson<TenderBulkResult>(
                `${TENDERS_ENDPOINT}/tenders/bulk?entity_id=${entityId}`,
                'POST',
                body,
            ),
        onSuccess: () => invalidateTenders(qc),
    })
}

export function useBulkDeleteTenders(entityId: string) {
    const bulk = useTenderBulk(entityId)
    return {
        ...bulk,
        mutateAsync: (ids: string[]) => bulk.mutateAsync({ ids, action: 'delete' }),
    }
}

export function useBulkReassignTenders(entityId: string) {
    const bulk = useTenderBulk(entityId)
    return {
        ...bulk,
        // Chargé de réponse et/ou responsable du marché : `useTenders.reaffectation`.
        mutateAsync: (v: CiblesDeReaffectation & { ids: string[] }) =>
            bulk.mutateAsync({ ids: v.ids, action: 'reassign', params: parametresDeReaffectation(v) }),
    }
}

export function useBulkChangeTenderStatus(entityId: string) {
    const bulk = useTenderBulk(entityId)
    return {
        ...bulk,
        mutateAsync: (v: {
            ids: string[]
            statusId: string
            outcomeReasonId?: string | null
            outcomeNotes?: string | null
        }) =>
            bulk.mutateAsync({
                ids: v.ids,
                action: 'set_status',
                params: {
                    status_id: v.statusId,
                    ...(v.outcomeReasonId ? { outcome_reason_id: v.outcomeReasonId } : {}),
                    ...(v.outcomeNotes ? { outcome_notes: v.outcomeNotes } : {}),
                },
            }),
    }
}

/**
 * Scrubbing RGPD — les zones de TEXTE LIBRE partent, les faits restent.
 *
 * Un acheteur public est une personne morale : son nom n'est pas une donnée
 * personnelle. Les seules PII possibles sont les notes internes (« rappeler
 * Mme X au 06… ») et le nom de l'attributaire concurrent. Les événements de
 * statut, les montants et les motifs sont CONSERVÉS — ce sont les faits qui
 * nourrissent les statistiques, et les effacer fausserait le taux de réussite
 * de l'organisme sans protéger personne.
 *
 * 🔴 La route existait depuis la livraison du module et n'avait AUCUN appelant,
 * alors que les trois autres fiches qui l'exposent (apprenants, intervenants,
 * non-conformités) l'offrent depuis leur menu. Un droit d'effacement qui n'est
 * exerçable que par appel d'API n'est pas exerçable.
 */
export function useAnonymizeTender(tenderId: string, entityId: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: () =>
            sendJson<void>(
                `${TENDERS_ENDPOINT}/tenders/${tenderId}/anonymize?entity_id=${entityId}`,
                'POST',
            ),
        onSuccess: () => invalidateTenders(qc, tenderId),
    })
}

// ── TND-006 — échéances ──────────────────────────────────────────────────────

export function useTenderDeadlines(tenderId: string | null, entityId: string) {
    return useQuery<TenderDeadline[]>({
        queryKey: tendersKeys.tenders.deadlines(tenderId ?? ''),
        queryFn: () =>
            getJson<TenderDeadline[]>(
                `${TENDERS_ENDPOINT}/tenders/${tenderId}/deadlines?entity_id=${entityId}`,
            ),
        enabled: Boolean(tenderId && entityId),
    })
}

/**
 * Le socle commun des écritures d'échéance.
 *
 * 🔴 **Invalide la FICHE, pas seulement la liste des échéances.** Le service
 * tient `tender_deadlines(offers_limit)` et `tenders.offers_deadline_at`
 * synchronisés dans les deux sens : corriger la date limite de remise depuis
 * cet onglet change le compte à rebours de l'en-tête et la colonne d'urgence du
 * listing. Ne rafraîchir que la liste laissait l'en-tête afficher l'ancienne
 * date — sur la donnée dont tout le module dépend.
 */
function useDeadlineMutation<TVars, TData = unknown>(
    tenderId: string,
    fn: (vars: TVars) => Promise<TData>,
) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: fn,
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: tendersKeys.tenders.deadlines(tenderId) })
            // La checklist juge la fraîcheur des attestations À LA DATE DE
            // REMISE : déplacer cette date change ce qui est périmé.
            qc.invalidateQueries({ queryKey: tendersKeys.tenders.piecesRoot(tenderId) })
            invalidateTenders(qc, tenderId)
        },
    })
}

export function useCreateDeadline(tenderId: string, entityId: string) {
    return useDeadlineMutation<{
        kind: string
        due_at: string
        label?: string | null
        is_hard?: boolean
    }>(tenderId, (body) =>
        sendJson<TenderDeadline>(
            `${TENDERS_ENDPOINT}/tenders/${tenderId}/deadlines?entity_id=${entityId}`,
            'POST',
            body,
        ),
    )
}

/**
 * TND-006 §5.2 — l'utilisateur déclare un FAIT, le serveur applique la règle.
 *
 * 🔴 **Le corps ne porte JAMAIS `due_at`.** C'est le calcul serveur qui rend la
 * date prouvable a posteriori (« Argalis m'avait alerté sur la bonne date,
 * calculée sur la bonne base ») ; une saisie libre ne prouverait rien, et le
 * schéma la refuse (`extra="forbid"`).
 *
 * Trois faits, deux échéances :
 * `motifs_requested` → délai de réponse de l'acheteur (+ 15 j) ;
 * `award_notice_published` → forclusion du référé contractuel (parution au
 * **JOUE** + 31 j) ; `contract_concluded` → le même kind par le régime de repli
 * (+ 6 mois), parce qu'un avis publié au seul BOAMP ne fait pas courir les 31 j.
 */
export function useArmDeadlineFromEvent(tenderId: string, entityId: string) {
    return useDeadlineMutation<
        {
            event: 'motifs_requested' | 'award_notice_published' | 'contract_concluded'
            occurred_at: string
        },
        TenderDeadline | null
    >(tenderId, (body) =>
        sendJson<TenderDeadline>(
            `${TENDERS_ENDPOINT}/tenders/${tenderId}/deadlines/from-event` +
                `?entity_id=${entityId}`,
            'POST',
            body,
        ),
    )
}

/**
 * ⚠️ `kind` est ABSENT du corps, et ce n'est pas un oubli : le serveur le refuse
 * (`extra="forbid"`). Changer la nature d'une échéance en ferait une autre, avec
 * d'autres rappels et d'autres gardes — on la supprime et on la recrée.
 */
export function useUpdateDeadline(tenderId: string, entityId: string) {
    return useDeadlineMutation<{
        deadlineId: string
        body: { due_at?: string; label?: string | null; is_hard?: boolean }
    }>(tenderId, ({ deadlineId, body }) =>
        sendJson<TenderDeadline>(
            `${TENDERS_ENDPOINT}/tenders/${tenderId}/deadlines/${deadlineId}` +
                `?entity_id=${entityId}`,
            'PATCH',
            body,
        ),
    )
}

export function useDeleteDeadline(tenderId: string, entityId: string) {
    return useDeadlineMutation<string>(tenderId, (deadlineId) =>
        sendJson<void>(
            `${TENDERS_ENDPOINT}/tenders/${tenderId}/deadlines/${deadlineId}` +
                `?entity_id=${entityId}`,
            'DELETE',
        ),
    )
}

/**
 * Acquitter STOPPE les rappels restants — et dé-acquitter ne les relance pas.
 *
 * Le `done` part en paramètre d'URL parce que la route le lit là : un corps JSON
 * serait ignoré et l'échéance repasserait toujours à « faite ».
 */
export function useAcknowledgeDeadline(tenderId: string, entityId: string) {
    return useDeadlineMutation<{ deadlineId: string; done: boolean }>(
        tenderId,
        ({ deadlineId, done }) =>
            sendJson<{ done: boolean }>(
                `${TENDERS_ENDPOINT}/tenders/${tenderId}/deadlines/${deadlineId}/acknowledge` +
                    `?entity_id=${entityId}&done=${done}`,
                'POST',
            ),
    )
}

/**
 * Rattache une tâche à l'échéance, APRÈS que le module Notifications l'a créée.
 *
 * Aucune tâche n'est créée ici : cet EPIC consomme le module de tâches, il n'en
 * devient jamais propriétaire.
 *
 * 🔴 Ce hook portait une « marque anti-doublon » : un pointeur UNIQUE qui
 * faisait disparaître l'entrée « Nouvelle tâche » dès la première. Le
 * rattachement est **idempotent et NON exclusif** depuis la migration 676 — les
 * tâches s'accumulent, et le rail en affiche le COMPTE.
 *
 * La réponse rend les deux compteurs À JOUR : l'écran peut repeindre la
 * pastille sans attendre le rechargement de la liste, et surtout sans
 * additionner tout seul un « +1 » qui serait faux dès que la tâche rattachée
 * l'était déjà, ou n'existe plus.
 */
export function useLinkDeadlineTask(tenderId: string, entityId: string) {
    return useDeadlineMutation<{ deadlineId: string; taskId: string }>(
        tenderId,
        ({ deadlineId, taskId }) =>
            sendJson<{ task_id: string; task_count: number; open_task_count: number }>(
                `${TENDERS_ENDPOINT}/tenders/${tenderId}/deadlines/${deadlineId}/link-task` +
                    `?entity_id=${entityId}`,
                'POST',
                { task_id: taskId },
            ),
    )
}


// ── TND-007 — checklist des pièces ───────────────────────────────────────────

/**
 * La checklist ET ses deux gardes, en une réponse.
 *
 * `atDate` est la date à laquelle on JUGE la fraîcheur des attestations : la
 * REMISE prévue, pas aujourd'hui. Une attestation valable ce matin et périmée
 * le jour du dépôt doit s'annoncer périmée — c'est ce jour-là que l'acheteur la
 * regardera. Elle fait donc partie de la clé de cache : deux dates, deux
 * réponses légitimement différentes.
 */
export function useTenderChecklist(
    tenderId: string | null,
    entityId: string,
    atDate?: string | null,
) {
    return useQuery<TenderChecklist>({
        queryKey: tendersKeys.tenders.pieces(tenderId ?? '', atDate),
        queryFn: () => {
            const params = new URLSearchParams({ entity_id: entityId })
            if (atDate) params.set('at_date', atDate)
            return getJson<TenderChecklist>(
                `${TENDERS_ENDPOINT}/tenders/${tenderId}/pieces?${params}`,
            )
        },
        enabled: Boolean(tenderId && entityId),
    })
}

function useChecklistMutation<T>(tenderId: string, entityId: string, fn: (body: T) => Promise<unknown>) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: fn,
        // `onSettled`, succès OU refus : un refus (DUME exclusif, pli figé) dit un écran PÉRIMÉ
        // — un autre onglet a basculé le DUME, le marché vient d'être attribué. Sans relecture,
        // « Exiger » restait offert au clic suivant, voué au même refus.
        onSettled: () => {
            // La racine, pas une date : la checklist se lit sous plusieurs dates de jugement.
            qc.invalidateQueries({ queryKey: tendersKeys.tenders.piecesRoot(tenderId) })
            // La garde de dépôt conditionne la transition « Déposé » : la fiche relit ses droits.
            qc.invalidateQueries({ queryKey: tendersKeys.tenders.detailAny(tenderId) })
            // Le détail d'une analyse sert `rangs_exclus_dume` et `paquets_vises` sur l'état de la
            // checklist ; `analyses(id)` est le préfixe de chaque détail (bascule DUME comprise).
            qc.invalidateQueries({ queryKey: tendersKeys.tenders.analyses(tenderId) })
            void entityId
        },
    })
}

export function useCreatePiece(tenderId: string, entityId: string) {
    return useChecklistMutation<Record<string, unknown>>(tenderId, entityId, (body) =>
        sendJson<TenderPiece>(
            `${TENDERS_ENDPOINT}/tenders/${tenderId}/pieces?entity_id=${entityId}`,
            'POST',
            body,
        ),
    )
}

export function useUpdatePiece(tenderId: string, entityId: string) {
    return useChecklistMutation<{ pieceId: string; patch: Record<string, unknown> }>(
        tenderId,
        entityId,
        ({ pieceId, patch }) =>
            sendJson<TenderPiece>(
                `${TENDERS_ENDPOINT}/tenders/${tenderId}/pieces/${pieceId}?entity_id=${entityId}`,
                'PATCH',
                patch,
            ),
    )
}

export function useDeletePiece(tenderId: string, entityId: string) {
    return useChecklistMutation<string>(tenderId, entityId, (pieceId) =>
        sendJson(
            `${TENDERS_ENDPOINT}/tenders/${tenderId}/pieces/${pieceId}?entity_id=${entityId}`,
            'DELETE',
        ),
    )
}

/** Joint le document qui PORTE la pièce, ou l'en détache avec `null`. */
export function useAttachPieceFile(tenderId: string, entityId: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ pieceId, gedFileId }: { pieceId: string; gedFileId: string | null }) =>
            sendJson<TenderPiece>(
                `${TENDERS_ENDPOINT}/tenders/${tenderId}/pieces/${pieceId}/file?entity_id=${entityId}`,
                'PUT',
                { ged_file_id: gedFileId },
            ),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: tendersKeys.tenders.piecesRoot(tenderId) })
            // Le dossier documentaire montre le même fichier : délier une pièce
            // le rend supprimable, et le bouton doit cesser d'être refusé.
            qc.invalidateQueries({ queryKey: tendersKeys.tenders.documentsRoot(tenderId) })
        },
    })
}

/** Recalcule la checklist depuis le gabarit de la procédure — FUSION DOUCE. */
export function useSeedChecklist(tenderId: string, entityId: string) {
    return useChecklistMutation<void>(tenderId, entityId, () =>
        sendJson<TenderChecklist>(
            `${TENDERS_ENDPOINT}/tenders/${tenderId}/pieces/seed?entity_id=${entityId}`,
            'POST',
            {},
        ),
    )
}

/** Bascule DUME ⇄ DC1+DC2. Aucun fichier n'est perdu : on bascule `required`. */
export function useCandidatureMode(tenderId: string, entityId: string) {
    return useChecklistMutation<boolean>(tenderId, entityId, (useDume) =>
        sendJson<TenderChecklist>(
            `${TENDERS_ENDPOINT}/tenders/${tenderId}/pieces/candidature-mode?entity_id=${entityId}`,
            'POST',
            { use_dume: useDume },
        ),
    )
}

// ── TND-008 — dossier documentaire ───────────────────────────────────────────

/**
 * La liste des pièces, PAR PAGES (C181-bis) : `data` garde la forme d'une réponse
 * unique pour les cinq lecteurs, et l'onglet Documents lit en plus `hasNextPage` /
 * `fetchNextPage`. Les options et leurs raisons vivent dans `useTenders.documentsPages`.
 */
export function useTenderDocuments(
    tenderId: string | null,
    entityId: string,
    filters?: FiltresDesDocuments,
) {
    return useInfiniteQuery(optionsDesDocuments(tenderId, entityId, filters, getJson))
}

/**
 * Le dépôt part en `multipart/form-data` : on ne pose donc PAS de
 * `Content-Type` à la main — le navigateur doit y écrire la frontière du
 * multipart, et l'imposer produit un corps que le serveur ne sait pas découper.
 */
export function useDepositTenderDocument(tenderId: string, entityId: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: async ({
            file,
            folderId,
            category,
            forceOnDuplicate,
        }: {
            file: File
            folderId?: string | null
            category?: string
            forceOnDuplicate?: boolean
        }) => {
            const form = new FormData()
            form.append('file', file)
            /* 🔴 **`category` n'est plus envoyée « AUTRE » en dur.**
               Le serveur sert une liste de catégories (`deposit_categories`)
               que le front jetait : TOUTE pièce d'appel d'offres entrait donc
               au coffre sous « Autre », y compris le règlement de consultation
               que l'acheteur vient d'envoyer. La demander à l'utilisateur EN
               PLUS du dossier aurait été deux questions pour une décision : elle
               se déduit du dossier, côté serveur (`FOLDER_CATEGORY`), là où elle
               est écrite. Omettre la clé, c'est demander cette déduction ;
               l'imposer reste possible pour un appelant qui, lui, sait. */
            form.append(
                'metadata',
                JSON.stringify({
                    folder_id: folderId ?? null,
                    ...(category ? { category } : {}),
                    force_on_duplicate: Boolean(forceOnDuplicate),
                }),
            )
            const res = await apiFetch(
                `${TENDERS_ENDPOINT}/tenders/${tenderId}/documents?entity_id=${entityId}`,
                { method: 'POST', body: form },
            )
            if (!res.ok) await handleTenderError(res)
            return res.json()
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: tendersKeys.tenders.documentsRoot(tenderId) })
        },
    })
}

/**
 * Retire un document du dossier.
 *
 * Deux refus possibles, et ils ne disent pas la même chose : la rétention d'un
 * marché gagné (409) et le fait que le fichier soit joint à une pièce de la
 * checklist (409, en NOMMANT la pièce). L'appelant doit rendre le message tel
 * quel — il dit par où délier.
 */
export function useDeleteTenderDocument(tenderId: string, entityId: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (fileId: string) =>
            sendJson(
                `${TENDERS_ENDPOINT}/tenders/${tenderId}/documents/${fileId}?entity_id=${entityId}`,
                'DELETE',
            ),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: tendersKeys.tenders.documentsRoot(tenderId) })
            // Une pièce pouvait pointer ce fichier : sa ligne doit cesser
            // d'annoncer un document déposé.
            qc.invalidateQueries({ queryKey: tendersKeys.tenders.piecesRoot(tenderId) })
        },
    })
}

/**
 * Range un document dans un AUTRE dossier de la MÊME consultation.
 *
 * 🔴 **Pourquoi une route du module AO et pas `PATCH /ged/files/{id}`.** La
 * route générique du coffre refuse toute cible SYSTÈME (DOC-003bis Phase 2), et
 * les cinq dossiers d'un appel d'offres le sont tous : un fichier déposé sans
 * dossier n'était donc rangeable par PERSONNE. La porte dédiée connaît son
 * domaine et garde ce qui doit l'être — la cible doit appartenir à CETTE
 * consultation.
 */
export function useMoveTenderDocument(tenderId: string, entityId: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ fileId, folderId }: { fileId: string; folderId: string }) =>
            sendJson(
                `${TENDERS_ENDPOINT}/tenders/${tenderId}/documents/${fileId}/folder?entity_id=${entityId}`,
                'PATCH',
                { folder_id: folderId },
            ),
        onSuccess: () => {
            // Les COMPTEURS des dossiers changent aussi, et ils vivent dans la
            // même réponse : n'invalider que la liste filtrée laisserait « DCE
            // reçu 3 » à côté d'un dossier qui en contient quatre.
            qc.invalidateQueries({ queryKey: tendersKeys.tenders.documentsRoot(tenderId) })
        },
    })
}


// ── TND-002 — lots ───────────────────────────────────────────────────────────

export function useTenderLots(tenderId: string | null, entityId: string) {
    return useQuery<TenderLotDetail[]>({
        queryKey: tendersKeys.tenders.lots(tenderId ?? ''),
        queryFn: () =>
            getJson<TenderLotDetail[]>(
                `${TENDERS_ENDPOINT}/tenders/${tenderId}/lots?entity_id=${entityId}`,
            ),
        enabled: Boolean(tenderId && entityId),
    })
}

function useLotMutation<T>(tenderId: string, fn: (arg: T) => Promise<unknown>) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: fn,
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: tendersKeys.tenders.lots(tenderId) })
            // Le nombre de lots figure sur la LIGNE du listing et sur la fiche :
            // les deux doivent tomber, sinon la colonne « Lots » reste sur son
            // ancien compte jusqu'à un rechargement complet.
            qc.invalidateQueries({ queryKey: tendersKeys.tenders.detailAny(tenderId) })
            qc.invalidateQueries({ queryKey: LIST_KEY, refetchType: 'all' })
            // Le bordereau est organisé PAR LOT : supprimer un lot emporte sa
            // grille, en ajouter un en ouvre une vide.
            qc.invalidateQueries({ queryKey: tendersKeys.tenders.priceGrid(tenderId) })
            // …et ses pièces propres, les affectations de l'équipe, le lot de ses échanges : la
            // checklist, l'équipe et les échanges ne montrent plus ce qu'il a emporté (ni son ancien nom).
            for (const cle of [tendersKeys.tenders.piecesRoot, tendersKeys.tenders.team, tendersKeys.tenders.questions])
                qc.invalidateQueries({ queryKey: cle(tenderId) })
        },
    })
}

export function useCreateLot(tenderId: string, entityId: string) {
    return useLotMutation<Record<string, unknown>>(tenderId, (body) =>
        sendJson<TenderLotDetail>(
            `${TENDERS_ENDPOINT}/tenders/${tenderId}/lots?entity_id=${entityId}`,
            'POST',
            body,
        ),
    )
}

export function useUpdateLot(tenderId: string, entityId: string) {
    return useLotMutation<{ lotId: string; patch: Record<string, unknown> }>(
        tenderId,
        ({ lotId, patch }) =>
            sendJson<TenderLotDetail>(
                `${TENDERS_ENDPOINT}/tenders/${tenderId}/lots/${lotId}?entity_id=${entityId}`,
                'PATCH',
                patch,
            ),
    )
}

/**
 * Supprime un lot. `force` n'est PAS un contournement de garde : le serveur refuse
 * TOUJOURS d'abord en NOMMANT ce qui serait perdu (lignes, pièces, affectations), et
 * l'écran ne repasse avec `force` qu'après un « oui » explicite de l'utilisateur.
 */
export function useDeleteLot(tenderId: string, entityId: string) {
    return useLotMutation<{ lotId: string; force?: boolean }>(tenderId, ({ lotId, force }) =>
        sendJson(
            `${TENDERS_ENDPOINT}/tenders/${tenderId}/lots/${lotId}` +
                `?entity_id=${entityId}${force ? '&force=true' : ''}`,
            'DELETE',
        ),
    )
}

// ── TND-010 — bordereau de prix ──────────────────────────────────────────────

export function useTenderPriceGrid(tenderId: string | null, entityId: string) {
    return useQuery<TenderPriceGrid>({
        queryKey: tendersKeys.tenders.priceGrid(tenderId ?? ''),
        queryFn: () =>
            getJson<TenderPriceGrid>(
                `${TENDERS_ENDPOINT}/tenders/${tenderId}/price-grid?entity_id=${entityId}`,
            ),
        enabled: Boolean(tenderId && entityId),
    })
}

/**
 * Les SEPT mutations du bordereau, qui n'existaient pas.
 *
 * 🔴 **Huit routes serveur sur neuf n'avaient aucun appelant.** Créer, modifier,
 * supprimer, réordonner une ligne de prix, reprendre les lignes d'un autre lot,
 * régler la révision d'un lot : tout était livré, testé, documenté — et
 * inatteignable. L'écran du bordereau était cent quatre-vingt-dix-huit lignes de
 * lecture pure, sans un seul champ de saisie.
 *
 * C'est la « passerelle » du module qui était coupée à sa source : gagner un
 * appel d'offres déclenche un magicien qui crée le client et ses contrats cadres
 * À PARTIR des lignes de bordereau. Sans elles, il n'a rien à reprendre.
 *
 * Toutes invalident la grille — c'est la seule lecture du module, et le serveur
 * y recalcule les totaux DQE : les rejouer côté écran les ferait diverger.
 */
function usePriceLineMutation<TVars, TData = unknown>(
    tenderId: string,
    fn: (vars: TVars) => Promise<TData>,
) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: fn,
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: tendersKeys.tenders.priceGrid(tenderId) })
            // Le nombre de lignes conditionne la suppression d'un lot : la fiche
            // et la liste des lots doivent le relire.
            qc.invalidateQueries({ queryKey: tendersKeys.tenders.lots(tenderId) })
        },
    })
}

export function useCreatePriceLine(tenderId: string, entityId: string) {
    return usePriceLineMutation<{ lotId: string; body: Record<string, unknown> }>(
        tenderId,
        ({ lotId, body }) =>
            sendJson<TenderPriceLine>(
                `${TENDERS_ENDPOINT}/tenders/${tenderId}/lots/${lotId}/price-lines?entity_id=${entityId}`,
                'POST',
                body,
            ),
    )
}

export function useUpdatePriceLine(tenderId: string, entityId: string) {
    return usePriceLineMutation<{
        lotId: string
        lineId: string
        patch: Record<string, unknown>
    }>(tenderId, ({ lotId, lineId, patch }) =>
        sendJson<TenderPriceLine>(
            `${TENDERS_ENDPOINT}/tenders/${tenderId}/lots/${lotId}/price-lines/${lineId}?entity_id=${entityId}`,
            'PATCH',
            patch,
        ),
    )
}

export function useDeletePriceLine(tenderId: string, entityId: string) {
    return usePriceLineMutation<{ lotId: string; lineId: string }>(
        tenderId,
        ({ lotId, lineId }) =>
            sendJson<void>(
                `${TENDERS_ENDPOINT}/tenders/${tenderId}/lots/${lotId}/price-lines/${lineId}?entity_id=${entityId}`,
                'DELETE',
            ),
    )
}

/** ⚠️ **PUT**, et non POST comme le réordonnancement des lignes de devis. */
export function useReorderPriceLines(tenderId: string, entityId: string) {
    return usePriceLineMutation<{ lotId: string; ordered_ids: string[] }>(
        tenderId,
        ({ lotId, ordered_ids }) =>
            sendJson<{ moved: number }>(
                `${TENDERS_ENDPOINT}/tenders/${tenderId}/lots/${lotId}/price-lines/reorder?entity_id=${entityId}`,
                'PUT',
                { ordered_ids },
            ),
    )
}

/**
 * Reprendre les lignes d'un AUTRE lot (deux lots partagent couramment l'essentiel de leurs
 * prestations). La réponse NOMME les lignes arrivées sans leur élément et celles laissées au
 * lot source (D191) : `annoncerLaReprise` les dit à l'écran.
 */
export function useDuplicateFromLot(tenderId: string, entityId: string) {
    return usePriceLineMutation<{ lotId: string; source_lot_id: string }, RepriseDUnLot | null>(
        tenderId,
        ({ lotId, source_lot_id }) =>
            sendJson<RepriseDUnLot>(
                `${TENDERS_ENDPOINT}/tenders/${tenderId}/lots/${lotId}/price-lines/duplicate-from-lot?entity_id=${entityId}`,
                'POST',
                { source_lot_id },
            ),
    )
}

/**
 * Les paramètres de révision du lot — LUS par le magicien de transformation,
 * écrits par personne jusqu'ici. Forme inverse du piège maison « un champ écrit
 * que rien ne relit » : ici on relisait un champ que rien n'écrivait, et le
 * magicien pré-remplissait consciencieusement quatre valeurs toujours nulles.
 */
export function useUpdateLotRevision(tenderId: string, entityId: string) {
    return usePriceLineMutation<{ lotId: string; patch: Record<string, unknown> }>(
        tenderId,
        ({ lotId, patch }) =>
            sendJson<Record<string, unknown>>(
                `${TENDERS_ENDPOINT}/tenders/${tenderId}/lots/${lotId}/revision?entity_id=${entityId}`,
                'PATCH',
                patch,
            ),
    )
}

/**
 * Le lot IMPLICITE d'un marché NON alloti.
 *
 * Le bordereau vit toujours sous un lot, y compris quand l'acheteur n'en a
 * déclaré aucun : sans ce geste, un marché non alloti — la moitié des
 * consultations — n'aurait nulle part où poser ses prix. Idempotent.
 */
export function useEnsureSingleLot(tenderId: string, entityId: string) {
    // `useLotMutation` et non la variante « ligne de prix » : ce geste CRÉE un
    // lot. Le compte de lots figure sur la fiche et sur la ligne du listing —
    // sans leur invalidation, ils resteraient sur « 0 lot » après le clic.
    return useLotMutation<void>(tenderId, () =>
        sendJson<{ lot_id: string; created: boolean }>(
            `${TENDERS_ENDPOINT}/tenders/${tenderId}/lots/ensure-single?entity_id=${entityId}`,
            'POST',
            {},
        ),
    )
}

// ── TND-009 — échanges avec l'acheteur ───────────────────────────────────────

export function useTenderQuestions(tenderId: string | null, entityId: string) {
    return useQuery<TenderQuestion[]>({
        queryKey: tendersKeys.tenders.questions(tenderId ?? ''),
        // Enveloppe paginée `{ items, next_cursor, has_more, counts }` : on la
        // déballe ICI, une fois, pour que l'écran reçoive un TABLEAU — comme
        // pour le référentiel des procédures, et pour la même raison. TOUTES les
        // pages : un registre de preuve se lit en entier (`useTenders.questionsPages`).
        queryFn: () => lireTousLesEchanges(tenderId ?? '', entityId, getJson),
        enabled: Boolean(tenderId && entityId),
    })
}

/**
 * Le socle commun des écritures d'échange.
 *
 * La pastille du parcours compte les échanges EN ATTENTE (`counts`) : elle vit
 * dans la réponse de la liste, donc l'invalidation de `questions` suffit à la
 * remettre juste. La fiche, elle, ne bouge que pour les gestes qui touchent
 * autre chose — ils l'invalident eux-mêmes.
 */
function useQuestionMutation<TVars>(tenderId: string, fn: (vars: TVars) => Promise<unknown>) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: fn,
        onSuccess: () =>
            qc.invalidateQueries({ queryKey: tendersKeys.tenders.questions(tenderId) }),
    })
}

/**
 * Consigne un échange. **Rien n'est envoyé à l'acheteur** : la question part du
 * profil acheteur, cette ligne en garde la trace datée.
 *
 * `asked_at` est DÉCLARATIF — la date de l'échange sur le profil, pas celle de
 * la saisie. C'est elle qui fait foi si le délai de réponse réglementaire est
 * discuté plus tard.
 */
export function useCreateQuestion(tenderId: string, entityId: string) {
    return useQuestionMutation<Record<string, unknown>>(tenderId, (body) =>
        sendJson<TenderQuestion>(
            `${TENDERS_ENDPOINT}/tenders/${tenderId}/questions?entity_id=${entityId}`,
            'POST',
            body,
        ),
    )
}

/**
 * ⚠️ Ni `kind` ni `direction` : ils décrivent un fait passé, et le serveur les
 * refuse. Une demande reçue de l'acheteur ne devient pas une question posée par
 * l'organisme — on supprime le brouillon, ou on clôt l'échange et on en consigne
 * un juste.
 */
export function useUpdateQuestion(tenderId: string, entityId: string) {
    return useQuestionMutation<{ questionId: string; body: Record<string, unknown> }>(
        tenderId,
        ({ questionId, body }) =>
            sendJson<TenderQuestion>(
                `${TENDERS_ENDPOINT}/tenders/${tenderId}/questions/${questionId}` +
                    `?entity_id=${entityId}`,
                'PATCH',
                body,
            ),
    )
}

/**
 * Consigne la réponse REÇUE sur le profil acheteur.
 *
 * Cocher « modifie le DCE » n'écrit qu'un drapeau — il ne s'éteint jamais, et
 * c'est l'acquittement qui le rend silencieux. Les pièces jointes s'AJOUTENT en
 * union dédupliquée : consigner une réponse n'efface pas celles de la question.
 */
export function useAnswerQuestion(tenderId: string, entityId: string) {
    const qc = useQueryClient()
    return useQuestionMutation<{ questionId: string; body: Record<string, unknown> }>(
        tenderId,
        async ({ questionId, body }) => {
            const res = await sendJson<TenderQuestion>(
                `${TENDERS_ENDPOINT}/tenders/${tenderId}/questions/${questionId}/answer` +
                    `?entity_id=${entityId}`,
                'POST',
                body,
            )
            // Une réponse qui modifie le DCE fait apparaître la bannière sur la
            // synthèse : la fiche doit la relire.
            if (body.dce_impact) invalidateTenders(qc, tenderId)
            return res
        },
    )
}

/** Supprime un BROUILLON. Un échange consigné se clôt, il ne s'efface pas. */
export function useDeleteQuestion(tenderId: string, entityId: string) {
    return useQuestionMutation<string>(tenderId, (questionId) =>
        sendJson<void>(
            `${TENDERS_ENDPOINT}/tenders/${tenderId}/questions/${questionId}` +
                `?entity_id=${entityId}`,
            'DELETE',
        ),
    )
}

/**
 * Matérialise une demande de pièce : pièce + échéance + tâche, en UNE
 * transaction serveur.
 *
 * Les trois surfaces créées vivent dans trois onglets différents — checklist,
 * échéances, tâches. N'en rafraîchir qu'une donnerait à croire que le reste n'a
 * pas été fait.
 */
export function useCreatePieceFromQuestion(tenderId: string, entityId: string) {
    const qc = useQueryClient()
    return useQuestionMutation<{ questionId: string; body: Record<string, unknown> }>(
        tenderId,
        async ({ questionId, body }) => {
            const res = await sendJson<{ piece_id: string; deadline_id: string; task_id: string }>(
                `${TENDERS_ENDPOINT}/tenders/${tenderId}/questions/${questionId}/create-piece` +
                    `?entity_id=${entityId}`,
                'POST',
                body,
            )
            qc.invalidateQueries({ queryKey: tendersKeys.tenders.piecesRoot(tenderId) })
            qc.invalidateQueries({ queryKey: tendersKeys.tenders.deadlines(tenderId) })
            invalidateTenders(qc, tenderId)
            return res
        },
    )
}

/**
 * Raccourci « Demande de justificatifs attributaire ».
 *
 * Le délai vient de l'acheteur, jamais d'un calcul : un justificatif non fourni
 * dans les temps fait passer le marché au candidat suivant. L'échéance posée est
 * donc ÉLIMINATOIRE par nature, et le serveur refuse le geste hors du stage
 * « Attribué sous réserve » — l'écran doit expliquer ce refus, pas le cacher.
 */
export function useAwardEvidenceRequest(tenderId: string, entityId: string) {
    const qc = useQueryClient()
    return useQuestionMutation<Record<string, unknown>>(tenderId, async (body) => {
        const res = await sendJson<Record<string, string>>(
            `${TENDERS_ENDPOINT}/tenders/${tenderId}/questions/award-request` +
                `?entity_id=${entityId}`,
            'POST',
            body,
        )
        qc.invalidateQueries({ queryKey: tendersKeys.tenders.piecesRoot(tenderId) })
        qc.invalidateQueries({ queryKey: tendersKeys.tenders.deadlines(tenderId) })
        invalidateTenders(qc, tenderId)
        return res
    })
}


/**
 * Acquitte l'impact d'une réponse sur le DCE.
 *
 * Tant que l'acquittement n'est pas posé, l'écran RAPPELLE que l'acheteur a
 * modifié le dossier de consultation : tout ce qui a été rédigé avant peut
 * être caduc. C'est une reconnaissance de lecture, pas un simple masquage.
 */
export function useAckDceImpact(tenderId: string, entityId: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (questionId: string) =>
            sendJson(
                `${TENDERS_ENDPOINT}/tenders/${tenderId}/questions/${questionId}` +
                    `/ack-dce-impact?entity_id=${entityId}`,
                'POST',
                {},
            ),
        onSuccess: () =>
            qc.invalidateQueries({ queryKey: tendersKeys.tenders.questions(tenderId) }),
    })
}



// ── TND-021 — tableau de bord ────────────────────────────────────────────────

/**
 * L'onglet « Tableau de bord » est-il seulement pertinent ?
 *
 * Il reste MASQUÉ tant qu'aucune affaire n'est décidée : un tableau de bord
 * vide n'apprend rien et fait douter du produit — mieux vaut ne pas le montrer
 * que de montrer des tirets. C'est le SERVEUR qui tranche (`dashboard_visible`),
 * pas un compte fait à l'écran : la règle doit être la même pour l'API.
 */
export function useTenderStatsAvailability(entityId: string) {
    return useQuery<{ decided_count: number; dashboard_visible: boolean }>({
        queryKey: tendersKeys.tenders.statsAvailability(entityId),
        queryFn: () =>
            getJson(`${TENDERS_ENDPOINT}/tenders/stats/availability?entity_id=${entityId}`),
        enabled: Boolean(entityId),
        staleTime: 5 * 60 * 1000,
    })
}

// ── TND-017 — veille BOAMP ───────────────────────────────────────────────────

export function useWatchProfiles(entityId: string) {
    return useQuery<WatchProfile[]>({
        queryKey: tendersKeys.tenderWatch.profiles(entityId),
        queryFn: () =>
            getJson<WatchProfile[]>(
                `${TENDERS_ENDPOINT}/tender-watch/profiles?entity_id=${entityId}`,
            ),
        enabled: Boolean(entityId),
    })
}

/**
 * L'état de la veille : ce qui reste à trier, et la fraîcheur des données.
 *
 * `to_triage_count` compte les FICHES au seuil du pipeline (stage `VEILLE`),
 * qu'elles viennent du BOAMP ou d'une saisie. Dédupliqué par construction :
 * une fiche, une ligne.
 *
 * ⚠️ **Son prédécesseur comptait des appartenances, pas des avis.** Il sommait
 * les `new_count` par ligne de critères, or l'ingestion fusionnait les listes
 * matcheuses : un avis retenu par deux critères y figurait deux fois. Une
 * pastille qui promet dix éléments pour en ouvrir sept est pire qu'une absence
 * de pastille.
 *
 * Monté dans la barre latérale, donc sur tous les écrans : la route ne fait
 * qu'un `count(*)` et un `max()`, et le rafraîchissement est celui du socle de
 * navigation.
 */
export function useTendersToTriageCount(entityId: string) {
    return useQuery<TenderWatchSummary>({
        queryKey: tendersKeys.tenderWatch.summary(entityId),
        queryFn: () =>
            getJson<TenderWatchSummary>(
                `${TENDERS_ENDPOINT}/tender-watch/summary?entity_id=${entityId}`,
            ),
        enabled: Boolean(entityId),
    })
}

/**
 * Relance l'ingestion sans attendre le passage nocturne.
 *
 * Le serveur rend **202** : la passe interroge une API publique et écrit une
 * fiche par avis retenu, elle se compte en dizaines de secondes. On accuse
 * réception, et l'écran relit son état — d'où l'invalidation du résumé plutôt
 * qu'une lecture du résultat, qui n'existe pas encore.
 */
export function useForceWatchSync(entityId: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: () =>
            sendJson<{ accepted: boolean }>(
                `${TENDERS_ENDPOINT}/tender-watch/sync?entity_id=${entityId}`,
                'POST',
            ),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: tendersKeys.tenderWatch.all })
        },
    })
}

/* ⚠️ **Les cinq hooks du feed d'avis ont disparu (migration 660).**
 * `useWatchFeed`, `useDismissWatchItem`, `useRestoreWatchItem`,
 * `useImportWatchItem`, `useMarkWatchItemsViewed` interrogeaient six routes qui
 * n'existent plus : un avis capté EST une fiche au stage `VEILLE`, qui se lit,
 * se trie et se refuse par le tableau des appels d'offres. Ne pas les
 * réintroduire « pour un aperçu rapide » — c'est ce second inbox qu'on vient de
 * supprimer, et son `dismissed_reason` en texte libre qui rendait la tuile
 * « Motifs de non-réponse » aveugle. */

/* Appels d'offres d'un client (sous-onglet de la fiche, TND-022) : leur module à part, ce
 * fichier étant au socle des tailles. Ré-exporté ici pour ses importeurs. */
export { useClientTenders } from './useTenders.client'
